import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Clock, ClipboardList, Pill, Activity, Info } from "lucide-react";
import { PageLoading } from "@/components/shared";
import api from "@/lib/api";

const Section = ({ title, icon: Icon, children, testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
    <div className="mb-3 flex items-center gap-2"><Icon className="h-4.5 w-4.5 text-cyan-dark" /><h3 className="font-heading font-bold text-navy">{title}</h3></div>
    {children}
  </div>
);

export default function WorkerCareVisitDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get(`/care/worker/visits/${id}`).then((r) => setD(r.data)).catch(() => setD(false)); }, [id]);
  if (d === false) return <div className="py-20 text-center text-slate-400">Besuch nicht verfügbar.</div>;
  if (!d) return <PageLoading />;
  const { visit: v, patient, location: loc } = d;

  return (
    <div className="space-y-4" data-testid="worker-care-visit-detail">
      <button onClick={() => nav("/app/worker/care")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy"><ArrowLeft className="h-4 w-4" /> Meine Tour</button>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-600">{v.status_label}</span>
          <span className="font-mono text-[11px] text-slate-400">{v.visit_number}</span>
        </div>
        <h1 className="mt-2 font-heading text-xl font-extrabold text-navy">{patient?.name}</h1>
        <div className="mt-1 flex items-center gap-2 text-sm text-slate-500"><Clock className="h-4 w-4" />{v.scheduled_start}–{v.scheduled_end} · {v.visit_type_label} · {patient?.care_grade_label}</div>
        {v.secondary_worker_required && <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700">Zwei-Personen-Einsatz</div>}
      </div>

      {loc && <Section title="Adresse & Zugang" icon={MapPin} testid="wcare-location">
        <div className="text-sm font-semibold text-navy">{[loc.street, loc.house_number].filter(Boolean).join(" ")}</div>
        <div className="text-sm text-slate-500">{loc.postal_code} {loc.city}</div>
        {loc.access_instructions && <div className="mt-2 rounded-lg bg-slate-50 p-2 text-sm text-slate-600"><Info className="mr-1 inline h-3.5 w-3.5" />{loc.access_instructions}</div>}
      </Section>}

      <Section title="Aufgaben" icon={ClipboardList} testid="wcare-tasks">
        <div className="space-y-2">
          {d.task_snapshot.map((t, i) => (
            <div key={t.id} className="rounded-xl bg-slate-50 p-3 text-sm" data-testid={`wcare-task-${i}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-navy">{i + 1}. {t.title}</span>
                <span className="rounded-full bg-cyan/10 px-2 py-0.5 text-[10px] font-semibold text-cyan-dark">{t.task_type_label}</span>
              </div>
              {t.instructions && <div className="mt-1 text-xs text-slate-500">{t.instructions}</div>}
              {t.role_requirement !== "ANY" && <span className="mt-1 inline-block rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">{t.role_requirement}</span>}
            </div>
          ))}
          {d.task_snapshot.length === 0 && <div className="text-sm text-slate-400">Keine Aufgaben hinterlegt.</div>}
        </div>
        <div className="mt-3 rounded-lg bg-slate-50 p-2 text-center text-xs text-slate-400">Abhaken folgt (Phase 9). Aktuell nur Vorschau.</div>
      </Section>

      {d.medications?.length > 0 && <Section title="Medikation" icon={Pill} testid="wcare-medication">
        <div className="space-y-1.5">{d.medications.map((m, i) => <div key={i} className="flex justify-between text-sm"><span className="font-semibold text-navy">{m.name}</span><span className="text-slate-500">{[m.dose, m.frequency].filter(Boolean).join(" · ")}</span></div>)}</div>
      </Section>}

      {d.therapy?.length > 0 && <Section title="Therapie" icon={Activity} testid="wcare-therapy">
        <div className="space-y-1.5">{d.therapy.map((t, i) => <div key={i} className="text-sm text-navy">{t.title}</div>)}</div>
      </Section>}

      {d.care_instructions && <Section title="Pflegehinweise" icon={Info} testid="wcare-instructions"><div className="text-sm text-slate-600">{d.care_instructions}</div></Section>}
    </div>
  );
}
